import { useQuery } from '@tanstack/react-query';
import { Descriptions, Divider, Flex, Grid, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getTicket } from 'src/apis/hr_ticket/expenseTrackingRequestTicket.api';
import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import type {
  IExpenseTrackingRequestTicket,
  IExpenseTrackingRequestTicketDetail
} from 'src/types/hr_ticket/expenseTrackingRequestTicket.type';
import type { IEmployeeShort } from 'src/types/master_data/employee.type';
import { formatDate } from 'src/utils/utils';
import './ExpenseTrackingRequestTicketDetail.scss';
import { BtnHrHandle } from 'src/components/v2/Button';

const { Title, Text } = Typography;

export type OpenType = {
  isOpen: boolean;
  id?: number;
};

export type AttachOpenType = {
  isOpen: boolean;
};

const columns = function (): ColumnsType<IExpenseTrackingRequestTicketDetail> {
  const columnTitle: ColumnsType<IExpenseTrackingRequestTicketDetail> = [
    {
      title: 'Người thực hiện',
      dataIndex: 'employee',
      key: 'employee',
      width: '25%',
      render: (value: IEmployeeShort, record) => (
        <Flex vertical gap={5}>
          <Text strong>{record.employee.fullName}</Text>
        </Flex>
      )
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: '25%',
      align: 'right'
    }
  ];

  return columnTitle;
};

export default function ExpenseTrackingRequestTicketDetail() {
  const { setInvalidKey } = useContext(TicketContext);
  const { setExpenseTrackingRequestTicket, setLoading } = useContext(HrTicketDetailContext);
  const { ticketSelected } = useContext(AppContext);
  const [expenseTrackingRequest, setExpenseTrackingRequest] = useState<IExpenseTrackingRequestTicket>();

  const { id } = useParams();

  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  const expenseTrackingRequestTicketQuery = useQuery({
    queryKey: ['expenseTrackingRequestTicket', id],
    queryFn: () => getTicket(Number(id)),
    enabled: !!id && !!ticketSelected
  });

  useEffect(() => {
    if (expenseTrackingRequestTicketQuery.data) {
      setExpenseTrackingRequestTicket(expenseTrackingRequestTicketQuery.data);
      setLoading(expenseTrackingRequestTicketQuery.isFetching);

      setExpenseTrackingRequest(expenseTrackingRequestTicketQuery.data?.data);
      setInvalidKey(JSON.stringify(expenseTrackingRequestTicketQuery.data?.data));
    }
  }, [
    expenseTrackingRequestTicketQuery.data,
    expenseTrackingRequestTicketQuery.isFetching,
    setExpenseTrackingRequestTicket,
    setInvalidKey,
    setLoading
  ]);

  return (
    <div className='etc-detail-wrapper'>
      <BtnHrHandle type='expenseTrackingRequestTicket' />

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      <Flex justify='start'>
        <Title level={5}>Thông tin phiếu</Title>
      </Flex>
      <Descriptions
        className='etc-item-header'
        layout='horizontal'
        labelStyle={{ width: '135px' }}
        column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
      >
        <Descriptions.Item label='Mã phiếu'>
          <Text strong>{expenseTrackingRequestTicketQuery.data?.data?.code}</Text>
        </Descriptions.Item>
        <Descriptions.Item label='Trạng thái'>
          <HRTicketStatusTag ticketStatus={expenseTrackingRequestTicketQuery.data?.data.ticketStatus} />
        </Descriptions.Item>
        <Descriptions.Item label='Người tạo'>
          <Text>{expenseTrackingRequestTicketQuery.data?.data?.createdBy?.fullName}</Text>
        </Descriptions.Item>
        <Descriptions.Item label='Ngày tạo' span={colspan}>
          <Text strong>{formatDate(expenseTrackingRequestTicketQuery.data?.data?.createdDate)}</Text>
        </Descriptions.Item>
        <Descriptions.Item label='Bộ phận' span={colspan}>
          <Text>{expenseTrackingRequestTicketQuery.data?.data?.createdBy?.department?.name}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={'Lý do tăng ca'} span={colspan}>
          <Text>{expenseTrackingRequestTicketQuery.data?.data?.note || ''}</Text>
        </Descriptions.Item>
      </Descriptions>

      <Flex justify='space-between' align='center' className='etr-detail-list-heading'>
        <Title level={5} style={{ marginBottom: '0px' }}>
          Danh sách
        </Title>
      </Flex>
      <Table
        className='etr-confirm-table'
        columns={columns()}
        rowKey={(record) => String(record.id)}
        dataSource={expenseTrackingRequest?.expenseTrackingRequestTicketDetails}
        pagination={false}
        scroll={{ x: 400 }}
        bordered={false}
        size='middle'
      />
    </div>
  );
}
